import { useState } from 'react';
import { Heart, Reply, MoreHorizontal, Edit2, Trash2, Clock, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { TierBadge } from './TierBadge';

export const Comment = ({ 
  comment, 
  postId, 
  user, 
  isAdmin, 
  onReply, 
  onDelete, 
  onEdit, 
  onLike, 
  onSeekToTimestamp, 
  depth = 0, 
  isMixPost = false 
}) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showMenu, setShowMenu] = useState(false);
  
  const canModify = comment.user_id === user?.id || isAdmin;
  const isLiked = comment.likes?.includes(user?.id);
  const likeCount = comment.likes?.length || 0;
  const maxDepth = 2;
  const hasTimestamp = comment.timestamp_seconds !== null && comment.timestamp_seconds !== undefined;
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleReply = async () => {
    if (!replyContent.trim()) return;
    await onReply(postId, comment.id, replyContent);
    setReplyContent('');
    setShowReplyInput(false);
  };
  
  const handleEdit = async () => {
    await onEdit(postId, comment.id, editContent);
    setIsEditing(false);
  };

  return (
    <div 
      className={`${depth > 0 ? 'ml-8 pl-4 border-l-2' : ''}`}
      style={{ borderColor: depth > 0 ? 'rgba(212, 175, 55, 0.2)' : 'transparent' }}
      data-testid={`comment-${comment.id}`}
    >
      <div className="py-3 group">
        <div className="flex items-start gap-3">
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${comment.user_tier === 'elite' ? 'ring-2 ring-purple-500' : ''}`}
            style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
          >
            {(comment.user_name || 'U').charAt(0)}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-semibold text-sm" style={{ color: '#ffffff' }}>{comment.user_name}</span>
              {comment.user_tier && <TierBadge tier={comment.user_tier} />}
              
              {hasTimestamp && isMixPost && (
                <button
                  onClick={() => onSeekToTimestamp && onSeekToTimestamp(comment.timestamp_seconds)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors hover:bg-purple-500/30"
                  style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' }}
                  title="Click to jump to this timestamp"
                  data-testid="timestamp-badge"
                >
                  <Clock size={12} />
                  <span className="text-xs font-medium">{formatTime(comment.timestamp_seconds)}</span>
                </button>
              )}
              
              <span className="text-xs" style={{ color: '#666' }}>
                {new Date(comment.created_at).toLocaleDateString()}
              </span>
              {comment.updated_at && <span className="text-xs italic" style={{ color: '#666' }}>(edited)</span>}
            </div>
            
            {isEditing ? (
              <div className="space-y-2">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleEdit}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <p className="text-sm mb-2" style={{ color: '#e0e0e0' }}>{comment.content}</p>
            )}
            
            {!isEditing && (
              <div className="flex items-center gap-4 text-xs">
                <button 
                  onClick={() => onLike(postId, comment.id)}
                  className={`flex items-center gap-1 transition-colors ${isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                  data-testid="like-comment-btn"
                >
                  <Heart size={14} fill={isLiked ? 'currentColor' : 'none'} />
                  {likeCount > 0 && <span>{likeCount}</span>}
                </button>
                
                {depth < maxDepth && (
                  <button 
                    onClick={() => setShowReplyInput(!showReplyInput)}
                    className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
                    data-testid="reply-btn"
                  >
                    <Reply size={14} />
                    Reply
                  </button>
                )}
                
                {canModify && (
                  <div className="relative">
                    <button 
                      onClick={() => setShowMenu(!showMenu)}
                      className="text-gray-400 hover:text-white transition-colors p-1"
                    >
                      <MoreHorizontal size={14} />
                    </button>
                    
                    {showMenu && (
                      <div 
                        className="absolute left-0 top-6 z-10 rounded-lg shadow-lg py-1 min-w-[100px]"
                        style={{ background: '#1a1a1a', border: '1px solid #333' }}
                      >
                        {comment.user_id === user?.id && (
                          <button 
                            onClick={() => { setIsEditing(true); setShowMenu(false); }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-white/10 flex items-center gap-2"
                          >
                            <Edit2 size={12} /> Edit
                          </button>
                        )}
                        <button 
                          onClick={() => { onDelete(postId, comment.id); setShowMenu(false); }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-white/10 flex items-center gap-2 text-red-400"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {showReplyInput && (
              <div className="mt-3 flex gap-2">
                <Input
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  className="flex-1 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleReply()}
                />
                <Button size="sm" onClick={handleReply} disabled={!replyContent.trim()}>
                  <Send size={14} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-0">
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              postId={postId}
              user={user}
              isAdmin={isAdmin}
              onReply={onReply}
              onDelete={onDelete}
              onEdit={onEdit}
              onLike={onLike}
              onSeekToTimestamp={onSeekToTimestamp}
              isMixPost={isMixPost}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};
